import { FeedbackContext } from "@/components/common/feedback/feedback-context";
import { ElementFileUploadContext } from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import { ElementIconType } from "@/components/ui/icons";
import { ErrorHandlers } from "@/errors/components/error-handling-context";
import { AppDispatch, RootState } from "@/store/store";
import { CreateDialogProps, ToastReturn } from "@faro-lotv/flat-ui";
import { GUID, type DistanceUnitTypes } from "@faro-lotv/foundation";
import { IElement } from "@faro-lotv/ielement-types";
import { ApiClients, ProjectApi } from "@faro-lotv/service-wires";
import { ComponentType, ReactNode } from "react";

/** An async function to create a dialog */
export type CreateDialogFn = (options: CreateDialogProps) => Promise<boolean>;

type ContextMenuActionHandler = (
  args: ContextMenuActionHandlerArgs,
) => Promise<void>;

/** Different types a context menu's action can have. */
export enum ContextMenuActionType {
  adjustTrajectory = "adjustTrajectory",
  assignTags = "assignTags",
  copyAsLocationMarker = "copyAsLocationMarker",
  delete = "delete",
  deleteVideoRecording = "deleteVideoRecording",
  download = "download",
  downloadImage = "downloadImage",
  downloadVideoWalk = "downloadVideoWalk",
  addData = "addData",
  editWaypointColor = "editWaypointColor",
  editWaypointName = "editWaypointName",
  enableEditWaypoints = "enableEditWaypoints",
  editElement = "editElement",
  editName = "editName",
  editDate = "editDate",
  generateFloorplan = "generateFloorplan",
  moveDataSession = "moveDataSession",
  moveTimePoint = "moveTimePoint",
  moveWaypointToOtherArea = "moveWaypointToOtherArea",
  markSheetAsAligned = "markSheetAsAligned",
  openAlignmentWizard = "openAlignmentWizard",
  resetCloudPosition = "resetCloudPosition",
  scaleFloor = "scaleFloor",
  scansExport = "scansExport",
  setCloudAsGeoReferenced = "setCloudAsGeoReferenced",
}

export type NestedMenuHandlerFnArgs = {
  /** Id of the nested menu element */
  nestedMenuItemId?: GUID;

  /** Id of the menu item project tree. */
  menuItemId: GUID;

  /** Current state of the application. */
  state: RootState;

  /** Project API client */
  projectApi: ProjectApi;

  /** handler to manage upload elements from context menu */
  elementFileUploadContext: ElementFileUploadContext;
};

export type NestedMenuObject = {
  /** Title of the nested menu */
  title?: string;

  /** Options to be rendered in the nested menu */
  options: Array<{
    /** Id of the nested menu item */
    id?: GUID;

    /** Label of the nested menu item */
    label: string;

    /** Additional content to show to the right of the label (e.g. a badge) */
    labelRight?: ReactNode;

    /** Icon to be rendered in the nested menu item */
    icon?: ElementIconType;

    /** Tagline of the nested menu item */
    tagline?: string;

    /** Icon to be rendered in the nested menu item */
    onClick(props: NestedMenuHandlerFnArgs): void;
  }>;

  /** Function to decide if the nested menu should be rendered or not */
  shouldShowNestedMenu?(elementId: GUID, state: RootState): boolean;
};

type NestedMenuFn = (elementId: GUID, state: RootState) => NestedMenuObject;

export type ContextMenuActionCustomComponentProps = {
  /** The action being rendered */
  action: ContextMenuAction;
  /** Id of the element in the project tree. */
  elementId: GUID;
  /** Callback when an item in the context menu is clicked */
  onContextMenuItemClicked(elementId: GUID, action: ContextMenuAction): void;
  /** Function to close the context menu */
  closeContextMenu(): void;
  /** A flag specifying if the UI element triggering this action is dark themed */
  dark: boolean;
};

export type BaseContextMenuAction = {
  /** Additional content to show to the right of the label (e.g. a badge) */
  labelRight?: ReactNode;

  /** The optional secondary text displayed below the label */
  secondaryText?: string;

  /**
   * Should the action be disabled with the provided message?
   *
   * If not defined, the action is always enabled when visible.
   *
   * @param iElement The element to decide for if its node should be disabled.
   * @param state The current application state.
   * @returns A message to display as a tooltip if the action is disabled or `undefined` if it's enabled.
   */
  disabledMessageForNode?(
    iElement: IElement,
    state: RootState,
  ): string | undefined;

  /**
   * Function that determines if a tooltip should be displayed for the action.
   *
   * @param iElement The element on which action will be performed. It is used to determine if a tooltip message should be displayed
   * and if yes, what message should be displayed.
   * @param state The current application state.
   * @returns A message to display as a tooltip or undefined if no tooltip should be displayed.
   */
  tooltipMessage?(iElement: IElement, state: RootState): string | undefined;

  /** A nested menu which needs to be rendered on click of a menu item */
  nestedMenuProps?: NestedMenuObject | NestedMenuFn;

  /** Callback to handle click events on the menu item. */
  handler?: ContextMenuActionHandler;

  /**
   * Callback executed after a deletion action is performed
   *
   * @returns True if the element deletion is handled by the callback
   */
  handleElementDeletion?(element: IElement): boolean;
};

type StandardContextMenuAction = BaseContextMenuAction & {
  /** Icon to use for the action */
  icon: ElementIconType;

  /** Text to display */
  label: string;

  /** Type of the action */
  type: ContextMenuActionType;

  /** Optional custom component to render instead of ContextMenuActionItem */
  CustomComponent?: never;
};

type CustomContextMenuAction = BaseContextMenuAction & {
  /** Icon to use for the action (optional when using custom component) */
  icon?: ElementIconType;

  /** Text to display (optional when using custom component) */
  label?: string;

  /** Type of the action (optional when using custom component) */
  type?: ContextMenuActionType;

  /** Custom component to render instead of ContextMenuActionItem */
  CustomComponent: ComponentType<ContextMenuActionCustomComponentProps>;
};

export type ContextMenuAction =
  | StandardContextMenuAction
  | CustomContextMenuAction;

export type ContextMenuActionHandlerArgs = {
  /** Id of the element in the project tree. */
  elementID: GUID;

  /** Current state of the application. */
  state: RootState;

  /** A flag specifying if the UI element triggering this action is dark themed */
  dark: boolean;

  /** Function to update the state. */
  dispatch: AppDispatch;

  /** API clients to talk to the Sphere XG backends. */
  apiClients: ApiClients;

  /** An async function to create a dialog */
  createDialog: CreateDialogFn;

  /** Request feedback from the user. */
  requestFeedback: FeedbackContext["requestFeedback"];

  /** Display a toast to the user. */
  openToast: ToastReturn["openToast"];

  /** Error handlers to display errors to the user */
  errorHandlers: ErrorHandlers;

  /** handler to manage upload elements from context menu */
  elementFileUploadContext: ElementFileUploadContext;

  /** The selected unit from the user setting */
  distanceUnit: DistanceUnitTypes;

  /** Function to Disable/Enable the confirmation button (the button is enabled by default) */
  setConfirmButtonDisabled(isDisabled: boolean): void;

  /** Function to manually trigger a dialog confirmation */
  confirmDialog(): void;
};
